import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { TextField } from 'formik-mui';
import { Field, Form, Formik } from 'formik';
import { CreateEventDto } from '@sr/dto';
import { useSnack } from 'shared/ui/snack';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { useStateSelector } from 'shared/store';
import { useAddCase } from 'features/events/events-actions/event-actions.hook';
import { EventTaskType } from '@sr/dto/dist/src/enums/events';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { isDateBeforeToday } from 'utils/date-validation';
import { CompanyPickerField } from 'shared/ui/formik-fields/company-picker';

export type FormData = {
  content: string;
  companyId: number | '';
  type: EventTaskType | '';
  dueDate: Date | undefined;
};

type ValidationData = Partial<{ [x in keyof FormData]: string }>;

const initialValues: FormData = {
  content: '',
  companyId: '',
  type: EventTaskType.CASE,
  dueDate: undefined
};

type DialogProps = {
  open: boolean;
  onClose: () => void;
  onCaseAdded?: () => void;
};

const AddCaseDialog = (props: DialogProps) => {
  const {
    open,
    onClose,
    onCaseAdded
  } = props;

  const user = useStateSelector(store => store.auth.user);
  
  const addEvent = useAddCase();
  const { showError, showSuccess } = useSnack();

  const validate = (values: FormData): ValidationData => {
    let validationResult: ValidationData = {};

    if (!values.content)
      validationResult.content = 'Обязательное поле';

    if (!values.companyId)
      validationResult.companyId = 'Фирма обязательна';

    if (!values.dueDate)
      validationResult.dueDate = 'Дедлайн дела не может быть пустым';

    if (values.dueDate && isDateBeforeToday(values.dueDate))
      validationResult.dueDate = 'Дедлайн дела не может быть раньше текущей даты';

    return validationResult;
  };

  const handleSubmit = async (values: FormData) => {
    if (user === null) {
      throw new Error('Please login before adding events');
    }
    const newEvent: CreateEventDto = {
      taskType: EventTaskType.CASE,
      content: values.content,
      companyId: Number(values.companyId),
      // contactPersonId: values.contactPersonId,
      dueDate: values.dueDate
    };
  
    try {
      await addEvent(newEvent);
      showSuccess('Дело успешно добавлено');
      if (onCaseAdded) {
        onCaseAdded();
      }
    } catch (e) { 
      showError(`Ошибка добавления дела: ${extractNestJsErrorMessage(e)}`);
      throw e;
    }
  };


  return (
    <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Новое дело</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
        >
          {({ submitForm, isSubmitting, values, resetForm }: any) => (
            <Form>
              <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                <Field
                  name="dueDate"
                  component={DatePickerField}
                  label="Дедлайн дела"
                />

                <Field
                  name="companyId"
                  component={CompanyPickerField}
                  label="Фирма"
                  size="small"
                />
                
                <Field
                  name="content"
                  label="Текст"
                  multiline
                  rows={4}
                  component={TextField}
                />
              </Stack>
              <DialogActions sx={{ mt: 2 }}>
                <Button 
                  variant="contained"
                  autoFocus
                  onClick={submitForm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Подождите...' : 'Сохранить'}
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  disabled={isSubmitting}
                >
                  Закрыть
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddCaseDialog;
